import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Chip, type ChipTone, LoadingScreen } from '@cowoo/shared/ui-web';
import { formatDate, formatMoney, qk } from '@cowoo/shared';
import { api } from '../../lib/api';
import { EmptyState, FilterChips, ListCard, Page, Row, Table, Td, Th } from '../../ui';
import { ContractPanel } from './ContractPanel';

const CHIPS = ['all', 'active', 'cancelled', 'ended', 'terminated'] as const;
type ContractChip = (typeof CHIPS)[number];

export const STATUS_TONE: Record<string, ChipTone> = {
  active: 'success',
  cancelled: 'danger',
  ended: 'neutral',
  terminated: 'danger',
};

/** 19 % VAT; § 14 shows the net with the gross beside it (03 § 8). */
export const grossOf = (net: number): number => Math.round(net * 1.19);

/**
 * `/vertraege` (10-web-admin § 14).
 *
 * Owner-only, so there is no capability check inside the screen: the
 * navigation entry and the route are both gated on `contracts.office`, and the
 * server refuses anybody else.
 *
 * Seats read "4 von 6" on an office contract and "–" on everything else — a
 * FlexDesk has no seats to give away.
 */
export function Contracts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contractId } = useParams();
  const [chip, setChip] = useState<ContractChip>('all');

  const contracts = useQuery({
    queryKey: qk.admin.area('contracts', chip),
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/contracts', {
        params: { query: chip === 'all' ? {} : { status: chip } },
      });
      if (error) throw error;
      return data;
    },
  });

  if (contracts.isLoading) return <LoadingScreen label={t('common.loading')} />;

  const items = contracts.data?.items ?? [];

  return (
    <Page>
      <ListCard title={t('admin.contracts.title')}>
        <FilterChips
          value={chip}
          onChange={setChip}
          options={CHIPS.map((value) => ({
            value,
            label: t(`admin.contracts.chip.${value}`),
            count: contracts.data?.counts[value],
          }))}
        />

        {items.length === 0 ? (
          <EmptyState>{t('admin.contracts.empty')}</EmptyState>
        ) : (
          <Table
            head={
              <>
                <Th>{t('admin.contracts.column.number')}</Th>
                <Th>{t('admin.contracts.column.product')}</Th>
                <Th>{t('admin.contracts.column.object')}</Th>
                <Th>{t('admin.contracts.column.payer')}</Th>
                <Th>{t('admin.contracts.column.seats')}</Th>
                <Th>{t('admin.contracts.column.start')}</Th>
                <Th align="right">{t('admin.contracts.column.monthly')}</Th>
                <Th>{t('admin.contracts.column.status')}</Th>
              </>
            }
          >
            {items.map((contract) => (
              <Row key={contract.id} to={`/vertraege/${contract.id}`} selected={contract.id === contractId}>
                <Td bold>{contract.number ?? '—'}</Td>
                <Td>{t(`admin.members.product.${contract.productKey}`)}</Td>
                <Td>
                  {contract.resourceLabel
                    ? [contract.resourceLabel, contract.planName].filter(Boolean).join(' · ')
                    : '—'}
                </Td>
                <Td>{contract.payerName ?? '—'}</Td>
                <Td>
                  {contract.includedSeats === null
                    ? '—'
                    : t('admin.contracts.seatsOf', {
                        used: contract.seatsUsed,
                        total: contract.includedSeats,
                      })}
                </Td>
                <Td>{formatDate(contract.startDay)}</Td>
                <Td numeric>
                  {contract.monthlyNetCents === null ? '—' : formatMoney(contract.monthlyNetCents)}
                </Td>
                <Td>
                  <Chip
                    tone={STATUS_TONE[contract.status] ?? 'neutral'}
                    label={
                      contract.status === 'cancelled' && contract.endDay
                        ? t('admin.contracts.cancelledTo', { date: formatDate(contract.endDay) })
                        : t(`admin.contracts.chip.${contract.status}`)
                    }
                  />
                </Td>
              </Row>
            ))}
          </Table>
        )}
      </ListCard>

      {contractId ? <ContractPanel id={contractId} onClose={() => navigate('/vertraege')} /> : null}
    </Page>
  );
}
